import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, FormEvent, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import type { IconName } from '@/components/ui/Icon';
import { useAuth } from '@/hooks/useAuth';
import { useOpenAiStatus } from '@/hooks/useOpenAiStatus';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import { superAiApi } from '@/features/super-ai/api';
import type { AIMessage, AITicket, SourceRef } from '@/features/super-ai/types';
import { AiInlineSourcesBlock } from '@/features/file-hub/components/AiInlineSourcesBlock';
import {
  ensureConsultantBoot,
  readConsultantSession,
  writeConsultantSession,
  type ConsultantHistoryTab,
} from './consultantSessionStore';
import '@/styles/za180-consultant.css';

const CAPS: Array<{ title: string; text: string; icon: IconName }> = [
  { title: 'Файлы', text: 'DOCX, PDF, XLSX, изображения и безопасные вложения', icon: 'upload' },
  { title: 'Научные задачи', text: 'Структура, методы, публикации, письма, отчёты', icon: 'document' },
  { title: 'Голосовой ввод', text: 'Диктуйте запрос через микрофон браузера', icon: 'chat' },
  { title: 'Проект результата', text: 'Предпросмотр → изменение → утверждение', icon: 'shield' },
  { title: 'Эскалация', text: 'Если ответа нет — запрос автоматически уходит владельцу', icon: 'info' },
];

type LocalTicket = {
  id: string;
  who: 'user' | 'ai' | 'owner';
  kind: 'AI' | 'WAITING_OWNER' | 'OWNER';
  text: string;
  at: string;
  source_refs?: SourceRef[];
};

const hydrateFromStore = () => {
  const s = readConsultantSession();
  return {
    conversationId: s.conversationId,
    messages: s.messages,
    tickets: s.tickets,
    draft: s.draft,
    tab: s.tab,
    isBooting: !s.hasBooted,
  };
};

const ConsultantPage = () => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const { user, hasRole } = useAuth();
  const isOwner = hasRole('SYSTEM_OWNER');
  const { showUnconfiguredHint } = useOpenAiStatus(isOwner);
  const roleLabel = isOwner ? 'Владелец системы' : user?.fullName || 'Пользователь';
  const activeRole = user?.activeRole || (isOwner ? 'SYSTEM_OWNER' : 'USER');

  const initial = hydrateFromStore();
  const [conversationId, setConversationId] = useState<string | null>(initial.conversationId);
  const [messages, setMessages] = useState<AIMessage[]>(initial.messages);
  const [tickets, setTickets] = useState<AITicket[]>(initial.tickets);
  const [draft, setDraft] = useState(initial.draft);
  const [isSending, setIsSending] = useState(false);
  const [isBooting, setIsBooting] = useState(initial.isBooting);
  const [tab, setTab] = useState<ConsultantHistoryTab>(initial.tab);
  const [listening, setListening] = useState(false);
  const historyRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<{ stop: () => void } | null>(null);
  const activeRoleRef = useRef(activeRole);
  activeRoleRef.current = activeRole;

  const pendingOwner = useMemo(
    () => tickets.filter((t) => t.status === 'WAITING_OWNER').length,
    [tickets],
  );

  const applySnapshot = useCallback((next: ReturnType<typeof readConsultantSession>) => {
    setConversationId(next.conversationId);
    setMessages(next.messages);
    setTickets(next.tickets);
    setDraft(next.draft);
    setTab(next.tab);
    setIsBooting(!next.hasBooted);
  }, []);

  const boot = useCallback(async () => {
    setIsBooting(true);
    const role = activeRoleRef.current;
    try {
      const conv = await superAiApi.createConversation('Научный консультант', 'auto', role);
      const welcome: AIMessage = {
        id: 'seed-welcome',
        conversation_id: conv.id,
        actor: 'AI',
        text: 'Здравствуйте! Я научный онлайн-консультант платформы. Задайте вопрос по разделам, заявкам, публикациям или научным задачам. Если уверенного ответа нет, сформирую обращение владельцу.',
        source_refs: [],
        draft_id: null,
        created_at: new Date().toISOString(),
      };
      const list = await superAiApi.listTickets().catch(() => [] as AITicket[]);
      const next = {
        conversationId: conv.id,
        messages: [welcome],
        tickets: list,
        draft: '',
        tab: 'all' as ConsultantHistoryTab,
        hasBooted: true,
      };
      writeConsultantSession(next);
      applySnapshot(next);
    } catch {
      const offline: AIMessage = {
        id: 'seed-offline',
        conversation_id: 'local',
        actor: 'AI',
        text: 'Сервис консультанта временно недоступен. Попробуйте снова или откройте «Коммуникации».',
        source_refs: [],
        draft_id: null,
        created_at: new Date().toISOString(),
      };
      const next = {
        conversationId: null,
        messages: [offline],
        tickets: [] as AITicket[],
        draft: '',
        tab: 'all' as ConsultantHistoryTab,
        hasBooted: true,
      };
      writeConsultantSession(next);
      applySnapshot(next);
    } finally {
      setIsBooting(false);
    }
  }, [applySnapshot]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      recognitionRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void ensureConsultantBoot(boot).then(() => {
      if (cancelled) return;
      applySnapshot(readConsultantSession());
      setIsBooting(false);
    });
    return () => {
      cancelled = true;
    };
  }, [applySnapshot, boot]);

  useEffect(() => {
    // Avoid clobbering an in-flight boot with empty initial state.
    if (isBooting && messages.length === 0 && !readConsultantSession().hasBooted) return;
    writeConsultantSession({
      conversationId,
      messages,
      tickets,
      draft,
      tab,
      hasBooted: readConsultantSession().hasBooted || messages.length > 0,
    });
  }, [conversationId, messages, tickets, draft, tab, isBooting]);
  useEffect(() => {
    const el = historyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, tab]);

  const historyItems: LocalTicket[] = useMemo(() => {
    const fromMessages: LocalTicket[] = messages.map((m) => ({
      id: m.id,
      who: m.actor === 'USER' ? 'user' : m.actor === 'OWNER' ? 'owner' : 'ai',
      kind: m.actor === 'OWNER' ? 'OWNER' : m.actor === 'USER' ? 'AI' : 'AI',
      text: m.text,
      at: m.created_at,
      source_refs: m.source_refs,
    }));

    const fromTickets: LocalTicket[] = tickets.map((t) => ({
      id: t.id,
      who: t.status === 'OWNER_ANSWERED' ? 'owner' : 'ai',
      kind: t.status === 'WAITING_OWNER' ? 'WAITING_OWNER' : 'OWNER',
      text:
        t.status === 'OWNER_ANSWERED' && t.owner_answer
          ? `${t.question}\n\nОтвет владельца: ${t.owner_answer}`
          : t.status === 'WAITING_OWNER'
            ? `Обращение владельцу: ${t.question}`
            : t.question,
      at: t.updated_at || t.created_at,
    }));

    const merged = [...fromMessages];
    if (tab === 'incoming' || tab === 'wait' || tab === 'owner') {
      return fromTickets
        .filter((x) => {
          if (tab === 'wait') return x.kind === 'WAITING_OWNER';
          if (tab === 'owner' || tab === 'incoming') return x.kind === 'OWNER' || x.kind === 'WAITING_OWNER';
          return true;
        })
        .sort((a, b) => a.at.localeCompare(b.at));
    }
    if (tab === 'ai') return fromMessages.filter((x) => x.who === 'ai');
    return merged;
  }, [messages, tickets, tab]);

  const close = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(isOwner ? routes.owner : '/');
  };

  const newChat = async () => {
    setDraft('');
    await boot();
    notify('Новый диалог открыт');
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || isSending) return;
    setDraft('');
    setIsSending(true);

    const optimistic: AIMessage = {
      id: `local-${Date.now()}`,
      conversation_id: conversationId || 'local',
      actor: 'USER',
      text,
      source_refs: [],
      draft_id: null,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      let convId = conversationId;
      if (!convId) {
        const conv = await superAiApi.createConversation('Научный консультант', 'auto', activeRole);
        convId = conv.id;
        setConversationId(convId);
      }
      const result = await superAiApi.sendMessage(convId, text);
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== optimistic.id),
        result.user_message,
        result.ai_message,
      ]);
      if (/не\s+нашёл|не\s+уверен|обращени|владельц/i.test(result.ai_message.text)) {
        try {
          const ticket = await superAiApi.createTicket(text, result.ai_message.text, convId);
          setTickets((prev) => [ticket, ...prev]);
        } catch {
          /* optional escalation */
        }
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          conversation_id: conversationId || 'local',
          actor: 'AI',
          text: 'Не удалось получить ответ. Запрос можно передать владельцу через «Коммуникации».',
          source_refs: [],
          draft_id: null,
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const toggleVoice = () => {
    const SpeechRecognition =
      (window as unknown as { webkitSpeechRecognition?: new () => SpeRec }).webkitSpeechRecognition ||
      (window as unknown as { SpeechRecognition?: new () => SpeRec }).SpeechRecognition;
    type SpeRec = {
      lang: string;
      continuous: boolean;
      interimResults: boolean;
      onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
      onend: (() => void) | null;
      onerror: (() => void) | null;
      start: () => void;
      stop: () => void;
    };
    if (!SpeechRecognition) {
      notify('Голосовой ввод не поддерживается в этом браузере');
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = 'ru-RU';
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (ev) => {
      const said = ev.results?.[0]?.[0]?.transcript || '';
      if (said) setDraft((prev) => (prev ? `${prev} ${said}` : said));
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
  };

  return (
    <div className="za180-overlay open" role="dialog" aria-modal="true" aria-label="Научный онлайн-консультант AI">
      <div className="za180-shell">
        <header className="za180-head">
          <div className="za180-logo" aria-hidden="true">
            <Icon name="chat" size={28} strokeWidth={1.6} />
          </div>
          <div className="za180-title">
            <small>Zertteu Academy · {roleLabel}</small>
            <h2>Научный онлайн-консультант AI</h2>
            <p>Общий научный помощник платформы. Неизвестные запросы передаются владельцу через Коммуникации.</p>
            {showUnconfiguredHint ? (
              <p className="za180-openai-hint">OpenAI не настроен — ответы из базы знаний / RAG</p>
            ) : null}
          </div>
          <div className="za180-head-actions">
            <span className="za180-scope personal">Платформа · консультант</span>
            <button type="button" onClick={close} aria-label="Закрыть">
              ×
            </button>
          </div>
        </header>

        <div className="za180-consult-grid">
          <aside className="za180-consult-side">
            <button type="button" className="za180-newchat" onClick={() => void newChat()}>
              + Новый диалог
            </button>
            <h3>Что умеет консультант</h3>
            {CAPS.map((cap) => (
              <div key={cap.title} className="za180-cap">
                <span aria-hidden="true">
                  <Icon name={cap.icon} size={14} strokeWidth={1.7} />
                </span>
                <span>
                  <strong>{cap.title}</strong>
                  <small>{cap.text}</small>
                </span>
              </div>
            ))}
            <div className="za180-responsibility">
              <h4>Граница ответственности</h4>
              <p>
                Консультант не раскрывает чужие данные и не выдумывает факты. Если уверенного ответа нет,
                формируется обращение владельцу.
              </p>
            </div>
          </aside>

          <main className="za180-consult-main">
            <div className="za180-history" ref={historyRef}>
              {isBooting ? <div className="za180-empty">Загрузка рабочей зоны…</div> : null}
              {!isBooting && !historyItems.length ? (
                <div className="za180-empty">Начните диалог — задайте научный или служебный вопрос.</div>
              ) : null}
              {historyItems.map((item) => (
                <article
                  key={item.id}
                  className="za180-ticket"
                  style={
                    {
                      '--ticket':
                        item.who === 'user'
                          ? '#41d2ff'
                          : item.kind === 'WAITING_OWNER'
                            ? '#e5b13f'
                            : '#9e80ff',
                    } as CSSProperties
                  }
                >
                  <header>
                    <strong>
                      {item.who === 'user'
                        ? 'Вы'
                        : item.who === 'owner'
                          ? 'Владелец системы'
                          : 'Научный онлайн-консультант AI'}
                    </strong>
                    <small>{new Date(item.at).toLocaleString('ru-RU')}</small>
                  </header>
                  <p>{item.text}</p>
                  {item.who === 'ai' && item.source_refs?.length ? (
                    <AiInlineSourcesBlock sources={item.source_refs} />
                  ) : null}
                  {item.kind === 'WAITING_OWNER' ? (
                    <span className="za180-ticket-status wait" style={{ marginTop: 8 }}>
                      Ожидает владельца
                    </span>
                  ) : null}
                </article>
              ))}
            </div>

            <section className="za180-consult-compose">
              <form className="za180-compose-row" onSubmit={onSubmit}>
                <label className="za180-iconbtn" title="Прикрепить файл">
                  <Icon name="upload" size={18} strokeWidth={1.8} />
                  <input type="file" multiple hidden />
                </label>
                <button
                  type="button"
                  className={`za180-iconbtn voice${listening ? ' listening' : ''}`}
                  title="Голосовой ввод"
                  onClick={toggleVoice}
                >
                  <Icon name="chat" size={18} strokeWidth={1.8} />
                </button>
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Спросите или продиктуйте научную задачу…"
                  aria-label="Сообщение консультанту"
                />
                <button
                  type="submit"
                  className="za180-send"
                  disabled={isSending || !draft.trim()}
                >
                  Отправить
                </button>
              </form>
            </section>
          </main>

          <aside className="za180-consult-side">
            <h3>История и обращения</h3>
            <p style={{ margin: '0 0 10px', color: '#7999aa', fontSize: 12 }}>
              Ваши вопросы, ответы AI и обращения владельцу.
            </p>
            <div className="za180-tabs">
              {(
                [
                  ['all', 'Все'],
                  ['ai', 'Ответил AI'],
                  ['wait', 'Ожидает владельца'],
                  ['owner', 'Ответ владельца'],
                  ...(isOwner ? ([['incoming', `Входящие · ${pendingOwner}`]] as const) : []),
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={tab === id ? 'active' : ''}
                  onClick={() => setTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="za180-source">
              <header>
                <strong>Коммуникации</strong>
                <span className={`za180-status${pendingOwner ? ' warn' : ''}`}>
                  {pendingOwner} ожидают
                </span>
              </header>
              <p>
                Неотвеченные AI-запросы не теряются: они поступают владельцу как отдельный тип
                коммуникации.
              </p>
              <button
                type="button"
                className="za180-newchat"
                style={{ marginTop: 10, minHeight: 38 }}
                onClick={() => navigate(`${routes.owner}/communications/connections`)}
              >
                Открыть коммуникации
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ConsultantPage;
